/* eslint-disable react/no-array-index-key */
import titleCase from '@gumtree/shared/src/util/title-case';
import React, { useReducer, useCallback } from 'react';
import { Icon } from '@gumtree/ui-library';
import {
    TabHeaderContainer,
    ContentContainer,
    UlContent,
    Li,
    Name,
    Value,
    DesktopMotorsSpecsSection,
    marginBetweenTabTitles,
    MoreTabsButtonRight,
    MoreTabsButtonLeft,
} from './styles';
import Tab from './tab/tab';
import MotorsSpecs from '../motors-specs';

interface Props {
    attributeGroups: MotorsSpecs[];
}

const CHANGE_CURRENT_TAB = 'CHANGE_CURRENT_TAB';
const APPEND_WIDTH = 'APPEND_WIDTH';
const UPDATE_TAB_CONTAINER_WIDTH = 'UPDATE_TAB_CONTAINER_WIDTH';

export const changeCurrentTab = (currentTab) => ({
    type: CHANGE_CURRENT_TAB,
    payload: currentTab,
});

export const appendWidth = (width, index) => ({
    type: APPEND_WIDTH,
    payload: { width, index },
});

const updateTabContainerWidth = (containerWidth) => ({
    type: UPDATE_TAB_CONTAINER_WIDTH,
    payload: { containerWidth },
});

const initialState = {
    currentTab: 0,
    tabWidths: [],
    indicatorLocation: { left: 0, width: 0 },
    transformLeft: 0,
    containerWidth: 0,
    tabCount: 0,
    showArrowRight: false,
    totalTabWidths: 0,
};

const reducer = (state, action) => {
    const { type, payload } = action;
    switch (type) {
        case CHANGE_CURRENT_TAB: {
            const { tabWidths, containerWidth, totalTabWidths } = state;
            const newTab = payload === -1 ? state.currentTab : payload;

            if (tabWidths.length > 0) {
                const leftWidths = tabWidths.filter((_, i) => i < newTab);
                let left =
                    leftWidths.length > 0
                        ? leftWidths.reduce((acc, val) => acc + val + marginBetweenTabTitles, 0)
                        : 0;

                const nextTabWidth = tabWidths[newTab + 1] || 0;
                let transformLeft = state.transformLeft;

                const distanceFromLeftToBeOnShow =
                    left +
                    tabWidths[newTab] +
                    nextTabWidth +
                    (nextTabWidth > 0 ? 2 : 1) * marginBetweenTabTitles;
                transformLeft =
                    distanceFromLeftToBeOnShow > containerWidth
                        ? containerWidth - distanceFromLeftToBeOnShow
                        : 0;

                left = left + transformLeft;
                const differenceBetweenTotalAndDesiredWidth =
                    totalTabWidths - distanceFromLeftToBeOnShow;
                const showArrowRight =
                    differenceBetweenTotalAndDesiredWidth > 0 && totalTabWidths > containerWidth;

                return {
                    ...state,
                    currentTab: newTab,
                    indicatorLocation: { left, width: state.tabWidths[newTab] },
                    transformLeft,
                    showArrowRight,
                };
            }
            return { ...state, currentTab: newTab };
        }
        case APPEND_WIDTH: {
            const { width, index } = payload;
            const newTabWidths = [...state.tabWidths];
            newTabWidths[index] = width;
            return { ...state, tabWidths: newTabWidths };
        }
        case UPDATE_TAB_CONTAINER_WIDTH: {
            const totalTabWidths = state.tabWidths.reduce(
                (acc, val) => acc + val + marginBetweenTabTitles,
                0
            );
            return { ...state, ...payload, totalTabWidths };
        }
        default:
            return state;
    }
};
const DesktopMotorsSpecs: React.FC<Props> = ({ attributeGroups }) => {
    const [{ currentTab, indicatorLocation, transformLeft, showArrowRight }, dispatch] = useReducer(
        reducer,
        initialState
    );
    const containerRef = useCallback((container) => {
        if (container) {
            dispatch(updateTabContainerWidth(container.getBoundingClientRect().width));
        }
        dispatch(changeCurrentTab(0));
        if (container) {
            let timeoutId;
            const resizeListener = () => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    if (container) {
                        dispatch(updateTabContainerWidth(container.getBoundingClientRect().width));
                        dispatch(changeCurrentTab(-1));
                    }
                }, 150);
            };
            window.addEventListener('resize', resizeListener);
            return () => window.removeEventListener('resize', resizeListener);
        }
        return;
    }, []);

    const isAdditionalFeatures =
        attributeGroups[currentTab]?.label === 'Additional Features' ||
        attributeGroups[currentTab]?.keyOnly;

    return (
        <DesktopMotorsSpecsSection data-q="motor-attribute-section">
            <TabHeaderContainer indicatorLocation={indicatorLocation} ref={containerRef}>
                {attributeGroups.map((tab, i) => (
                    <Tab
                        key={i}
                        dispatch={dispatch}
                        index={i}
                        label={tab.label}
                        currentTab={currentTab}
                        transformLeft={transformLeft}
                    />
                ))}
                {!!transformLeft && (
                    <MoreTabsButtonLeft
                        aria-label="see more tabs"
                        onClick={() => dispatch(changeCurrentTab(currentTab - 1))}
                    >
                        <Icon type="chevron-l" size="small" />
                    </MoreTabsButtonLeft>
                )}
                {showArrowRight && (
                    <MoreTabsButtonRight
                        aria-label="see more tabs"
                        onClick={() => dispatch(changeCurrentTab(currentTab + 1))}
                    >
                        <Icon type="chevron-r" size="small" />
                    </MoreTabsButtonRight>
                )}
            </TabHeaderContainer>
            <ContentContainer>
                <UlContent isAdditionalFeatures={!!isAdditionalFeatures}>
                    {attributeGroups[currentTab]?.attributes.map(({ name, value, key, unit }) => (
                        <Li key={key}>
                            <Name
                                isAdditionalFeatures={!!isAdditionalFeatures}
                                data-q="car-attributes-name"
                            >
                                {titleCase(name)}
                            </Name>
                            {!isAdditionalFeatures && (
                                <Value data-q="car-attributes-value">
                                    {value}
                                    {unit && ` ${unit}`}
                                </Value>
                            )}
                        </Li>
                    ))}
                </UlContent>
            </ContentContainer>
        </DesktopMotorsSpecsSection>
    );
};

export default DesktopMotorsSpecs;
